import {Label} from '@/components/ui/label';
import {Input} from '@/components/ui/input';
import {Search, X, Check} from 'lucide-react';
import {RewardFormData, Language} from '../types';

interface FormStep3ContentProps {
    formData: RewardFormData;
    onChange: (data: Partial<RewardFormData>) => void;
    disabled: boolean;
    domains: any[];
    availableLanguages: Language[];
    languageSearch: string;
    onLanguageSearchChange: (search: string) => void;
    onAddLanguage: (code: string) => void;
    onRemoveLanguage: (code: string) => void;
}

export const FormStep3Content = ({
                                     formData,
                                     onChange,
                                     disabled,
                                     // domains,
                                     availableLanguages,
                                     languageSearch,
                                     onLanguageSearchChange,
                                     onAddLanguage,
                                     onRemoveLanguage
                                 }: FormStep3ContentProps) => {
    const filteredLanguages = availableLanguages.filter(lang =>
        lang.language.toLowerCase().includes(languageSearch.toLowerCase()) ||
        lang.code.toLowerCase().includes(languageSearch.toLowerCase())
    );

    return (
        <div className="space-y-4">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Configurações</h3>

            {/*<div className="space-y-2">*/}
            {/*    <Label htmlFor="domain_id">Domínio *</Label>*/}
            {/*    <select*/}
            {/*        id="domain_id"*/}
            {/*        value={formData.domain_id}*/}
            {/*        onChange={(e) => onChange({domain_id: parseInt(e.target.value)})}*/}
            {/*        disabled={disabled}*/}
            {/*        className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"*/}
            {/*    >*/}
            {/*        <option value="0">Selecione um domínio</option>*/}
            {/*        {domains?.map((domain) => (*/}
            {/*            <option key={domain.id} value={domain.id}>*/}
            {/*                {domain.name} ({domain.domain})*/}
            {/*            </option>*/}
            {/*        ))}*/}
            {/*    </select>*/}
            {/*</div>*/}

            <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                    <Label htmlFor="trigger_time">Delay (segundos)</Label>
                    <Input
                        id="trigger_time"
                        type="number"
                        min="0"
                        value={formData.trigger_time}
                        onChange={(e) => onChange({trigger_time: parseInt(e.target.value) || 0})}
                        placeholder="0"
                        disabled={disabled}
                    />
                    <p className="text-xs text-gray-500">Tempo de espera antes de exibir</p>
                </div>

                <div className="space-y-2">
                    <Label htmlFor="frequency_time">Frequência (minutos)</Label>
                    <Input
                        id="frequency_time"
                        type="number"
                        min="1"
                        value={formData.frequency_time}
                        onChange={(e) => onChange({frequency_time: parseInt(e.target.value) || 10})}
                        placeholder="10"
                        disabled={disabled}
                    />
                    <p className="text-xs text-gray-500">Intervalo entre exibições</p>
                </div>
            </div>

            <div className="space-y-2">
                <Label>Idiomas para Tradução *</Label>

                {/* Box de idiomas selecionados */}
                {formData.languages.length > 0 && (
                    <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg mb-3">
                        <div className="flex flex-wrap gap-2">
                            {formData.languages.map((langCode) => {
                                const lang = availableLanguages.find(l => l.code === langCode);
                                return lang ? (
                                    <div
                                        key={langCode}
                                        className="flex items-center gap-2 px-3 py-1 bg-white border border-blue-300 rounded-full text-sm"
                                    >
                                        <span className="font-medium">{lang.language}</span>
                                        <button
                                            type="button"
                                            onClick={() => onRemoveLanguage(langCode)}
                                            disabled={disabled}
                                            className="text-red-500 hover:text-red-700"
                                        >
                                            <X className="h-3 w-3"/>
                                        </button>
                                    </div>
                                ) : null;
                            })}
                        </div>
                    </div>
                )}

                {/* Campo de busca */}
                <div className="relative">
                    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400"/>
                    <Input
                        type="text"
                        placeholder="Buscar idioma..."
                        value={languageSearch}
                        onChange={(e) => onLanguageSearchChange(e.target.value)}
                        disabled={disabled}
                        className="pl-9"
                    />
                </div>

                {/* Lista de idiomas */}
                <div className="border border-gray-200 rounded-lg max-h-64 overflow-y-auto divide-y divide-gray-100">
                    {filteredLanguages.length === 0 ? (
                        <div className="p-4 text-center text-gray-500 text-sm">
                            Nenhum idioma encontrado
                        </div>
                    ) : (
                        filteredLanguages.map((lang) => {
                            const isSelected = formData.languages.includes(lang.code);
                            return (
                                <button
                                    key={lang.code}
                                    type="button"
                                    onClick={() => !isSelected && onAddLanguage(lang.code)}
                                    disabled={disabled || isSelected}
                                    className={`
                                        w-full px-4 py-3 text-left hover:bg-gray-50 transition-colors
                                        ${isSelected ? 'bg-gray-100 cursor-not-allowed opacity-50' : 'cursor-pointer'}
                                        ${disabled ? 'cursor-not-allowed' : ''}
                                    `}
                                >
                                    <div className="flex items-center justify-between">
                                        <span className="font-medium text-gray-900">{lang.language}</span>
                                        {isSelected && <Check className="h-4 w-4 text-blue-600"/>}
                                    </div>
                                    <span className="text-xs text-gray-500">{lang.code}</span>
                                </button>
                            );
                        })
                    )}
                </div>

                <p className="text-xs text-gray-500">
                    Selecione os idiomas para gerar traduções automáticas ({formData.languages.length} selecionados)
                </p>
            </div>

            <div className="space-y-2">
                <Label htmlFor="closed">Botão de Fechar</Label>
                <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                    <div className="flex items-center gap-2">
                        <div className={`h-2 w-2 rounded-full ${formData.closed ? 'bg-green-500' : 'bg-gray-400'}`}/>
                        <span className="text-sm font-medium text-gray-700">
                            {formData.closed ? 'Botão Visível' : 'Botão Oculto'}
                        </span>
                    </div>
                    <button
                        type="button"
                        onClick={() => onChange({closed: !formData.closed})}
                        disabled={disabled}
                        className={`
                            relative inline-flex h-6 w-11 items-center rounded-full
                            transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2
                            ${formData.closed ? 'bg-blue-600' : 'bg-gray-300'}
                            ${disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}
                        `}
                    >
                        <span
                            className={`
                                inline-block h-4 w-4 transform rounded-full bg-white transition-transform
                                ${formData.closed ? 'translate-x-6' : 'translate-x-1'}
                            `}
                        />
                    </button>
                </div>
                <p className="text-xs text-gray-500">
                    Quando ativado, o botão de fechar (X) será exibido no reward
                </p>
            </div>

            <div className="space-y-2">
                <Label htmlFor="show_branding">Informações da Empresa</Label>
                <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                    <div className="flex items-center gap-2">
                        <div
                            className={`h-2 w-2 rounded-full ${formData.show_branding ? 'bg-green-500' : 'bg-gray-400'}`}/>
                        <span className="text-sm font-medium text-gray-700">
                            {formData.show_branding ? 'Bloco Visível' : 'Bloco Oculto'}
                        </span>
                    </div>
                    <button
                        type="button"
                        onClick={() => onChange({show_branding: !formData.show_branding})}
                        disabled={disabled}
                        className={`
                            relative inline-flex h-6 w-11 items-center rounded-full
                            transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2
                            ${formData.show_branding ? 'bg-blue-600' : 'bg-gray-300'}
                            ${disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}
                        `}
                    >
                        <span
                            className={`
                                inline-block h-4 w-4 transform rounded-full bg-white transition-transform
                                ${formData.show_branding ? 'translate-x-6' : 'translate-x-1'}
                            `}
                        />
                    </button>
                </div>
                <p className="text-xs text-gray-500">
                    Exibe a logo do site no topo e o rodapé com política de privacidade e termos de uso
                </p>
            </div>
        </div>
    );
};